import { useEffect, useState } from 'react';
import { useAuth } from '@clerk/react';
import apiService from '../services/api';

const TOKEN_RETRY_DELAY_MS = 500;
const TOKEN_RETRY_LIMIT = 20;

export function AuthTokenSync({ children }: { children: React.ReactNode }): React.ReactElement | null {
  const { getToken, isSignedIn, isLoaded } = useAuth();

  const [ready, setReady] = useState(false);

  useEffect(() => {
    // Wait for Clerk to finish loading before doing anything
    if (!isLoaded) return;

    if (!isSignedIn) {
      apiService.setAuthToken(null);
      setReady(true);
      return;
    }

    setReady(false);

    let cancelled = false;

    // Register the refresher so ApiService can auto-retry 401s with a fresh token
    apiService.setTokenRefresher(getToken);

    let retryCount = 0;
    let retryTimeout: ReturnType<typeof setTimeout> | null = null;

    const syncToken = async () => {
      try {
        const token = await getToken();
        if (cancelled) return;

        // Clerk can briefly report signed-in before a session token is available.
        // Keep protected routes suspended so /api/me never fires without Authorization.
        if (!token) {
          if (retryCount < TOKEN_RETRY_LIMIT) {
            retryCount += 1;
            retryTimeout = setTimeout(syncToken, TOKEN_RETRY_DELAY_MS);
          } else {
            console.error('AuthTokenSync: Clerk returned no token for signed-in user');
            setReady(true);
          }
          return;
        }

        retryCount = 0;
        apiService.setAuthToken(token);
        setReady(true);
      } catch (err) {
        console.error('AuthTokenSync: failed to get token', err);
        if (!cancelled) setReady(true);
      }
    };

    syncToken();
    const interval = setInterval(syncToken, 30_000); // 30s — tighter than Clerk's 60s JWT expiry

    return () => {
      cancelled = true;
      if (retryTimeout) clearTimeout(retryTimeout);
      clearInterval(interval);
    };
  }, [getToken, isSignedIn, isLoaded]);

  if (!ready) return null;

  return <>{children}</>;
}
